import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowDown, ArrowUp, Flashlight, Loader2, Wallet, X, Zap } from "lucide-react";
import MarketHeroAvatar from "../../../../components/market/MarketHeroAvatar";
import {
  fetchBondPredictOrderBook,
  fetchBondPredictPortfolio,
  placeBondPredictOrder,
  resetBondPredictPlaceOrder,
  selectBondPredictOrderBookKey,
  selectBondPredictPlaceOrderStatus,
  selectBondPredictPortfolioPositions,
} from "../../../redux/bondPredictSlice";
import { getMarketTitle, MARKET_TYPES, formatChancePct } from "../../../utils/bondPredictHelpers";
import { getSelectedOutcomeLabel } from "../../../utils/bondPredictDetailHelpers";
import {
  buildOrderPayload,
  estimateOrderTotal,
  findPositionShares,
  formatTradeChancePct,
  formatTradeCoin,
  formatTradePrice01,
  formatTradeShares,
  getDefaultLimitPrice,
  getExecutableLevel,
  getPlaceOrderToast,
  ORDER_SIDES,
  ORDER_TYPES,
  validateOrderForm,
} from "../../../utils/bondPredictTradeHelpers";
import "../../../bond-predict-trade-modal.css";

const QUICK_SHARES = [1, 5, 10, 25, 50, 100, 250];

const MarketDetailOrderModal = ({
  open,
  onClose,
  market,
  selectedOutcome,
  orderBook,
  orderBookStatus,
  userCoin,
  initialSide = ORDER_SIDES.BUY,
  onPlaced,
}) => {
  const dispatch = useDispatch();
  const placeOrderStatus = useSelector(selectBondPredictPlaceOrderStatus);
  const portfolioPositions = useSelector(selectBondPredictPortfolioPositions);
  const orderBookKey = useSelector(selectBondPredictOrderBookKey);
  const priceTouchedRef = useRef(false);
  const handleCloseRef = useRef(null);

  const [orderType, setOrderType] = useState(ORDER_TYPES.LIMIT);
  const [side, setSide] = useState(ORDER_SIDES.BUY);
  const [shares, setShares] = useState("");
  const [price, setPrice] = useState("");
  const [formError, setFormError] = useState("");

  const marketTitle = getMarketTitle(market);
  const outcomeLabel = getSelectedOutcomeLabel(selectedOutcome, market);
  const expectedBookKey =
    market?._id && selectedOutcome?._id ? `${market._id}:${selectedOutcome._id}` : null;
  const userShares = useMemo(
    () => findPositionShares(portfolioPositions, market?._id, selectedOutcome?._id),
    [portfolioPositions, market?._id, selectedOutcome?._id]
  );

  const executable = getExecutableLevel(orderBook, side);
  const totalCoin = estimateOrderTotal({
    orderType,
    side,
    shares,
    price,
    orderBook,
  });

  const displayPrice = useMemo(() => {
    if (orderType === ORDER_TYPES.LIMIT && price !== "") {
      const n = Number(price);
      if (Number.isFinite(n)) return Math.min(1, Math.max(0, n));
    }
    if (executable.price != null) return executable.price;
    const fromOutcome =
      typeof selectedOutcome?.displayPrice === "number"
        ? selectedOutcome.displayPrice
        : null;
    return fromOutcome;
  }, [orderType, price, executable.price, selectedOutcome?.displayPrice]);

  const chancePct = useMemo(() => {
    if (displayPrice == null) return null;
    return Math.round(displayPrice * 100);
  }, [displayPrice]);

  useEffect(() => {
    if (!open) return;
    dispatch(fetchBondPredictPortfolio({ limit: 100, skip: 0 }));
  }, [open, dispatch]);

  useEffect(() => {
    if (!open || !market?._id || !selectedOutcome?._id) return;
    dispatch(
      fetchBondPredictOrderBook({ marketId: market._id, outcomeId: selectedOutcome._id })
    );
  }, [open, dispatch, market?._id, selectedOutcome?._id]);

  useEffect(() => {
    if (!open) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape" && placeOrderStatus !== "loading") {
        handleCloseRef.current?.();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, placeOrderStatus]);

  useEffect(() => {
    if (!open) {
      priceTouchedRef.current = false;
      return;
    }
    setOrderType(ORDER_TYPES.LIMIT);
    setShares("");
    setFormError("");
    priceTouchedRef.current = false;
    dispatch(resetBondPredictPlaceOrder());
    const nextSide = initialSide === ORDER_SIDES.SELL ? ORDER_SIDES.SELL : ORDER_SIDES.BUY;
    setSide(nextSide);

    const defaultPrice = getDefaultLimitPrice(selectedOutcome, orderBook, nextSide);
    setPrice(defaultPrice != null ? defaultPrice.toFixed(4) : "");
  }, [open, selectedOutcome?._id, initialSide, dispatch]);

  useEffect(() => {
    if (!open || priceTouchedRef.current) return;
    if (!expectedBookKey || orderBookKey !== expectedBookKey) return;

    const defaultPrice = getDefaultLimitPrice(selectedOutcome, orderBook, side);
    if (defaultPrice != null) {
      setPrice(defaultPrice.toFixed(4));
    }
  }, [open, expectedBookKey, orderBookKey, orderBook, side, selectedOutcome?._id]);

  const applySide = (nextSide) => {
    setSide(nextSide);
    if (priceTouchedRef.current) return;
    const defaultPrice = getDefaultLimitPrice(selectedOutcome, orderBook, nextSide);
    if (defaultPrice != null) {
      setPrice(defaultPrice.toFixed(4));
    }
  };

  const handleClose = () => {
    if (placeOrderStatus === "loading") return;
    dispatch(resetBondPredictPlaceOrder());
    onClose();
  };

  handleCloseRef.current = handleClose;

  const handlePriceChange = (value) => {
    priceTouchedRef.current = true;
    if (value === "") {
      setPrice("");
      return;
    }
    if (!/^\d*\.?\d*$/.test(value)) return;
    const num = Number(value);
    if (!Number.isFinite(num)) {
      setPrice(value);
      return;
    }
    if (num >= 0 && num <= 1) setPrice(value);
  };

  const handleSubmit = async () => {
    setFormError("");
    const validationError = validateOrderForm({
      orderType,
      side,
      shares,
      price,
      orderBook,
      userShares,
    });
    if (validationError) {
      setFormError(validationError);
      return;
    }

    if (!market?._id || !selectedOutcome?._id) {
      setFormError("Invalid market or outcome.");
      return;
    }

    const payload = buildOrderPayload({
      marketId: market._id,
      outcomeId: selectedOutcome._id,
      side,
      orderType,
      shares,
      price,
      orderBook,
    });

    const result = await dispatch(placeBondPredictOrder(payload));
    if (placeBondPredictOrder.rejected.match(result)) {
      setFormError(result.payload || "Could not place order.");
      return;
    }

    const data = result.payload || {};
    const toast = getPlaceOrderToast({
      side,
      orderType,
      fillResult: data.fillResult,
      message: data.message,
    });

    dispatch(resetBondPredictPlaceOrder());
    onClose();
    onPlaced?.(toast);
  };

  if (!open || !selectedOutcome) return null;

  const busy = placeOrderStatus === "loading";
  const bookLoading = orderBookStatus === "loading" && !orderBook;
  const isBuy = side === ORDER_SIDES.BUY;
  const sideClass = isBuy ? "buy" : "sell";
  const showMarketAvatar = market?.type !== MARKET_TYPES.HEAD_TO_HEAD;

  const modal = (
    <div
      className="bp-trade-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className={`bp-trade-modal bp-trade-modal-${sideClass}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bp-trade-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="bp-trade-modal-glow" aria-hidden />

        <header className="bp-trade-modal-head">
          <div className="bp-trade-modal-head-main">
            <span className="bp-trade-modal-kicker">Trade desk</span>
            <span className={`bp-trade-side-pill bp-trade-side-pill-${sideClass}`}>
              {isBuy ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
              {isBuy ? "Buy" : "Sell"} · {orderType === ORDER_TYPES.LIMIT ? "Limit" : "Market"}
            </span>
          </div>
          <button
            type="button"
            className="bp-trade-modal-close"
            onClick={handleClose}
            disabled={busy}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>

        <div className="bp-trade-modal-main">
          <aside className="bp-trade-modal-context">
            <div className="bp-trade-context-hero">
              {showMarketAvatar ? (
                <MarketHeroAvatar
                  market={market}
                  label={marketTitle}
                  size={52}
                  className="bp-trade-context-avatar"
                />
              ) : null}
              <div className="bp-trade-context-copy">
                <h2 id="bp-trade-modal-title">{outcomeLabel}</h2>
                {marketTitle && marketTitle !== outcomeLabel ? (
                  <p className="bp-trade-context-market">{marketTitle}</p>
                ) : null}
              </div>
            </div>

            <div className={`bp-trade-chance bp-trade-chance-${sideClass}`}>
              <div className="bp-trade-chance-top">
                <span>Implied chance</span>
                <strong className="tabular-nums">
                  {formatChancePct(displayPrice)}
                </strong>
              </div>
              <div className="bp-trade-chance-track" aria-hidden>
                <span
                  className="bp-trade-chance-fill"
                  style={{ width: `${chancePct ?? 0}%` }}
                />
              </div>
              <p className="bp-trade-context-book">
                {bookLoading ? (
                  "Loading order book…"
                ) : executable.price != null ? (
                  <>
                    {executable.label}{" "}
                    <strong className="tabular-nums">
                      {formatTradePrice01(executable.price)} ({formatTradeChancePct(executable.price)})
                    </strong>
                    {typeof executable.quantity === "number" ? (
                      <span> · {formatTradeShares(executable.quantity)} share</span>
                    ) : null}
                  </>
                ) : (
                  "No live bid/ask — limit orders may rest on book."
                )}
              </p>
            </div>

            <div className="bp-trade-context-balances">
              <div className="bp-trade-balance-card">
                <Wallet size={13} aria-hidden />
                <div>
                  <span>Wallet</span>
                  <strong className="tabular-nums">{formatTradeCoin(userCoin)}</strong>
                </div>
              </div>
              <div className="bp-trade-balance-card">
                <Zap size={13} aria-hidden />
                <div>
                  <span>You hold</span>
                  <strong className="tabular-nums">{formatTradeShares(userShares)} share</strong>
                </div>
              </div>
            </div>
          </aside>

          <section className="bp-trade-modal-form" aria-label="Order entry">
            <div className="bp-trade-form-toggles">
              <div className="bp-trade-side-toggle" role="tablist" aria-label="Order side">
                <button
                  type="button"
                  role="tab"
                  aria-selected={isBuy}
                  className={`bp-trade-side-btn bp-trade-side-buy${isBuy ? " bp-trade-side-btn-active" : ""}`}
                  onClick={() => applySide(ORDER_SIDES.BUY)}
                  disabled={busy}
                >
                  Buy
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isBuy}
                  className={`bp-trade-side-btn bp-trade-side-sell${!isBuy ? " bp-trade-side-btn-active" : ""}`}
                  onClick={() => applySide(ORDER_SIDES.SELL)}
                  disabled={busy}
                >
                  Sell
                </button>
              </div>

              <div className="bp-trade-type-toggle" role="tablist" aria-label="Order type">
                <button
                  type="button"
                  role="tab"
                  aria-selected={orderType === ORDER_TYPES.LIMIT}
                  className={`bp-trade-type-btn${orderType === ORDER_TYPES.LIMIT ? " bp-trade-type-btn-active" : ""}`}
                  onClick={() => setOrderType(ORDER_TYPES.LIMIT)}
                  disabled={busy}
                >
                  Limit
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={orderType === ORDER_TYPES.MARKET}
                  className={`bp-trade-type-btn${orderType === ORDER_TYPES.MARKET ? " bp-trade-type-btn-active" : ""}`}
                  onClick={() => setOrderType(ORDER_TYPES.MARKET)}
                  disabled={busy}
                >
                  Market
                </button>
              </div>
            </div>

            <div className="bp-trade-form-fields">
              <label className="bp-trade-field">
                <span>Shares</span>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={shares}
                  onChange={(e) => setShares(e.target.value.replace(/[^\d.]/g, ""))}
                  placeholder="0"
                  disabled={busy}
                />
              </label>

              {orderType === ORDER_TYPES.LIMIT ? (
                <label className="bp-trade-field">
                  <span>Limit (0–1)</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={price}
                    onChange={(e) => handlePriceChange(e.target.value)}
                    placeholder="0.6100"
                    disabled={busy}
                  />
                </label>
              ) : (
                <div className="bp-trade-market-slot">
                  <div className="bp-trade-market-note">
                    <Flashlight size={14} aria-hidden />
                    <span>Best available price on book</span>
                  </div>
                </div>
              )}
            </div>

            <div className="bp-trade-quick-picks">
              {QUICK_SHARES.map((n) => (
                <button
                  key={n}
                  type="button"
                  className="bp-trade-chip"
                  onClick={() => setShares(String(n))}
                  disabled={busy}
                >
                  {n}
                </button>
              ))}
              {!isBuy && userShares > 0 ? (
                <button
                  type="button"
                  className="bp-trade-chip bp-trade-chip-max"
                  onClick={() => setShares(String(userShares))}
                  disabled={busy}
                >
                  Max
                </button>
              ) : null}
            </div>

            <div className={`bp-trade-total bp-trade-total-${sideClass}`}>
              <span>Estimated {isBuy ? "cost" : "payout"}</span>
              <strong className="tabular-nums">
                {totalCoin != null ? formatTradeCoin(totalCoin) : "—"}
              </strong>
            </div>

            {formError ? <p className="bp-trade-error">{formError}</p> : null}

            <footer className="bp-trade-modal-foot">
              <button
                type="button"
                className="bp-trade-modal-ghost"
                onClick={handleClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`bp-trade-submit bp-trade-submit-${sideClass}`}
                onClick={handleSubmit}
                disabled={busy}
              >
                {busy ? (
                  <Loader2 size={15} className="bp-spin" aria-hidden />
                ) : (
                  <Zap size={15} aria-hidden />
                )}
                Place {isBuy ? "buy" : "sell"}
              </button>
            </footer>
          </section>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default MarketDetailOrderModal;
